import { Button, Text } from "design_system";
import { useEffect, useState } from "react";
import { ComposedModal } from "components/Modal/ComposedModal";
import { StackedEmptyModal } from "components/Modal";
import { Combobox, OptionObj, OptionType } from "components/Combobox";
import { toast } from "react-toastify";
import {
  getGraduationYearsOptions,
  getStudentPricingDiscountAttr,
} from "helpers/pricing";
import { STUDENT_DISCOUNT_PERCENT } from "constants/config";
import { ModalStackItem } from "store/slices/modalStack";
import { useModalStack } from "hooks/useModalStack/useModalStack";
import { useAuth } from "@clerk/nextjs";
import { useSubscriptionRedirect } from "../SubscriptionProvider/SubscriptionProvider";
import useSubscription from "features/User/hooks/useSubscription";
import { trackEventServerSide } from "helpers/api";
import { getMajorVersion } from "helpers/services/mixpanel";
import {
  MixpanelActionSource,
  MixpanelEvent,
  StudentDiscountModalViewedEvent,
  StudentDiscountModalClickedEvent,
  StudentDiscountModalDismissedEvent,
} from "helpers/services/mixpanel/events";
import {
  STUDENT_TRIAL_PERIOD_DAYS,
  GRAD_STUDENT_TRIAL_PERIOD_DAYS,
} from "constants/config";
import { SubscriptionPlan } from "enums/subscription-plans";
import useLabels from "hooks/useLabels";
import { HighFiveIcon } from "./HighFiveIcon";
import { logAuthError } from "features/auth/utils/authErrorLogging";
import { usePricingData, useSubscriptionPricing } from "features/Pricing";
import PricingFeatures from "features/Pricing/components/PricingFeatures";
import { BaseModalProps } from "store/slices/modalStack";
import { STUDENT_ROLES } from "features/auth/utils/roleUtils";

export const STUDENT_DISCOUNT_MODAL_ID = "student-discount-modal";

// Props interface for type-safe modal opening
export interface StudentDiscountModalProps extends BaseModalProps {
  type: "premium" | "deep";
  role?: (typeof STUDENT_ROLES)[number];
  actionSource?: MixpanelActionSource;
}

/**
 * Student discount modal that handles both Premium and Deep upgrade flows.
 * Type is determined by props passed through modal stack.
 */
function StudentDiscountModal() {
  const modal: ModalStackItem = {
    id: STUDENT_DISCOUNT_MODAL_ID,
    blocksScroll: true,
  };
  const { closeModal, isModalOpen, modalProps } =
    useModalStack<StudentDiscountModalProps>(modal);

  // Determine configuration based on modal type
  const modalType = modalProps?.type || "premium";
  const isDeepUpgrade = modalType === "deep";
  const role = modalProps?.role;
  const actionSource =
    modalProps?.actionSource || MixpanelActionSource.Onboarding;

  const labelPath = isDeepUpgrade
    ? "student-discount.deep-upgrade-modal"
    : "student-discount.modal";
  const subscriptionPlan = isDeepUpgrade
    ? SubscriptionPlan.DEEP
    : SubscriptionPlan.PREMIUM;
  const includeTrialPeriod = !isDeepUpgrade;
  const trialPeriodText = role === "graduate-student" ? "1 month" : "1 year";
  const modalName = "StudentDiscountModal";
  const { isSignedIn } = useAuth();
  const { subscription, isSubscriptionPremium } = useSubscription();
  const {
    redirectToCheckout,
    redirectToCustomerPortalSubscriptionUpdateConfirm,
  } = useSubscriptionRedirect();
  const [labels] = useLabels(labelPath);

  const graduationYearOptions = getGraduationYearsOptions({
    includeGraduationSuffix: false,
  });

  const [graduationYear, setGraduationYear] = useState<OptionType>(
    graduationYearOptions[0] || ""
  );
  const [isLoading, setIsLoading] = useState(false);

  // Determine trial period based on role
  const trialPeriodDays =
    role === "graduate-student"
      ? GRAD_STUDENT_TRIAL_PERIOD_DAYS
      : STUDENT_TRIAL_PERIOD_DAYS;

  // Fetch pricing data
  const { pricingData } = usePricingData();

  // Use the pricing hook for the specified plan
  const { baseYearlyPrice } = useSubscriptionPricing(subscriptionPlan);

  // Track modal viewed event
  useEffect(() => {
    if (!isModalOpen) {
      return;
    }

    const viewedEvent: StudentDiscountModalViewedEvent = {
      event: MixpanelEvent.StudentDiscountModalViewed,
      actionSource: actionSource,
      version: getMajorVersion(),
    };
    trackEventServerSide(viewedEvent);
  }, [isModalOpen, actionSource]);

  const handleClaimOffer = async (buttonText: string) => {
    // Track click event
    const clickedEvent: StudentDiscountModalClickedEvent = {
      event: MixpanelEvent.StudentDiscountModalClicked,
      actionSource: actionSource,
      value: buttonText,
      version: getMajorVersion(),
    };
    trackEventServerSide(clickedEvent);

    if (!isSignedIn) {
      closeModal();
      return;
    }

    if (baseYearlyPrice?.id) {
      // Apply student discount based on graduation year
      const discountAttrs = getStudentPricingDiscountAttr(
        (graduationYear as OptionObj).value
      );

      setIsLoading(true);

      try {
        // Check if this is Pro→Deep upgrade
        const isProToDeepUpgrade =
          subscriptionPlan === SubscriptionPlan.DEEP &&
          isSubscriptionPremium &&
          subscription?.user;

        if (isProToDeepUpgrade && subscription?.user) {
          // Use subscription update flow for existing Pro subscribers
          const subscriptionId = subscription.user.id;
          const subscriptionItemId = subscription.user.items?.data[0].id;

          if (subscriptionItemId) {
            await redirectToCustomerPortalSubscriptionUpdateConfirm({
              subscription: subscriptionId,
              subscriptionItemId,
              newPriceId: baseYearlyPrice.id,
              discountId: discountAttrs.discountId, // Pass student discount
              returnUrl: window.location.href,
            });
          }
        } else {
          // Use checkout flow for new subscriptions or free→Pro
          const checkoutOptions: any = {
            price: baseYearlyPrice.id,
            discountId: discountAttrs.discountId,
          };

          // Only include trial period for free→Pro flow
          if (includeTrialPeriod) {
            checkoutOptions.trialPeriodDays = trialPeriodDays;
          }

          await redirectToCheckout(checkoutOptions);
        }
      } catch (error) {
        // Log error to DataDog
        logAuthError({
          error,
          errorType: "checkout_redirect",
          hookName: modalName,
          context: {
            priceId: baseYearlyPrice.id,
            discountId: discountAttrs.discountId,
            graduationYear: (graduationYear as OptionObj).value,
          },
        });

        // Show user-friendly error message
        toast.error("Unable to redirect to checkout. Please try again.");

        setIsLoading(false);
      }
    }
  };

  const handleDismiss = () => {
    // Track dismissal event
    const dismissedEvent: StudentDiscountModalDismissedEvent = {
      event: MixpanelEvent.StudentDiscountModalDismissed,
      actionSource: actionSource,
      version: getMajorVersion(),
    };
    trackEventServerSide(dismissedEvent);

    closeModal();
  };

  const claimOfferButtonText = labels["claim-button"]?.replace(
    "{TRIAL_PERIOD}",
    trialPeriodText
  );

  // Determine which features to show based on plan
  const features =
    subscriptionPlan === SubscriptionPlan.DEEP
      ? pricingData?.deep?.features
      : pricingData?.premium?.features;

  return (
    <StackedEmptyModal modal={modal} onClose={closeModal}>
      <ComposedModal>
        <ComposedModal.Header
          title={labels["title"]?.replace("{TRIAL_PERIOD}", trialPeriodText)}
          onClose={closeModal}
        />
        <ComposedModal.Body>
          <div className="flex flex-col gap-6 pb-6 border-b border-border-base">
            <div className="flex items-center gap-6">
              <div className="flex-shrink-0">
                <HighFiveIcon />
              </div>

              <Text>
                {labels["description"]
                  ?.replace("{DISCOUNT}", STUDENT_DISCOUNT_PERCENT.toString())
                  ?.replace("{TRIAL_PERIOD}", trialPeriodText)}
              </Text>
            </div>

            <div className="flex flex-col gap-1">
              <Text as="label" size="sm" weight="medium">
                {labels["graduation-year-label"]}
              </Text>
              <Combobox
                options={graduationYearOptions}
                defaultSelected={graduationYear}
                onChange={(selected) =>
                  setGraduationYear(selected as OptionType)
                }
                multiple={false}
                size="sm"
              />
            </div>
          </div>

          <div className="flex flex-col gap-6 pt-6">
            <Text as="h3" weight="bold" size="base">
              {labels["features-title"]}
            </Text>

            <PricingFeatures featuresList={features ?? []} />
          </div>
        </ComposedModal.Body>

        <div className="flex flex-col gap-3 p-4 border-t">
          <Button
            variant="primary"
            size="base"
            onClick={() => handleClaimOffer(claimOfferButtonText)}
            disabled={isLoading}
            className="w-full justify-center"
          >
            {claimOfferButtonText}
          </Button>
          <Button
            variant="tertiary"
            size="base"
            onClick={handleDismiss}
            className="w-full justify-center"
          >
            {labels["continue-button"] || labels["continue-free-button"]}
          </Button>
        </div>
      </ComposedModal>
    </StackedEmptyModal>
  );
}

export default StudentDiscountModal;
